import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, CalendarIcon, EyeDropperIcon } from '@heroicons/react/24/outline';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';

const AccessibilityPolicyPage: React.FC = () => {
  const lastUpdated = 'December 15, 2024';
  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
              <EyeDropperIcon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Accessibility</h1>
          <p className="text-xl text-gray-600 mb-4">
            We are committed to an inclusive experience for everyone. Here is our approach and how to reach us.
          </p>
          <div className="flex items-center justify-center text-sm text-gray-500">
            <CalendarIcon className="w-4 h-4 mr-2" />
            Last updated: {lastUpdated}
          </div>
        </motion.div>

        {/* Commitment */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8"
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our commitment</h2>
          <ul className="space-y-3">
            {[
              'Design and build with WCAG 2.1 AA as our target standard.',
              'Maintain sufficient color contrast and keyboard operability.',
              'Provide meaningful labels, focus styles, and ARIA where appropriate.',
              'Continuously improve through audits and user feedback.'
            ].map((item) => (
              <li key={item} className="flex items-start">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span className="text-gray-600">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Known limitations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8"
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Known limitations</h2>
          <p className="text-gray-600">Some advanced canvas interactions may be harder to use with assistive technologies. We are exploring alternatives and shortcuts to improve usability.</p>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-8"
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Report an accessibility issue</h2>
          <p className="text-gray-600 mb-4">
            If you experience a barrier, please contact us at
            {' '}<a className="text-blue-600 underline" href="mailto:support@stylesync.app">support@stylesync.app</a>.
            Include the page, what you tried to do, and any assistive tech used.
          </p>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Request an accessible format</h3>
          <p className="text-gray-600">We can provide content in alternative formats upon request. We’ll work with you to meet your needs.</p>
        </motion.div>
      </main>

      <AppFooter />
    </div>
  );
};

export default AccessibilityPolicyPage;


